import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  LinearProgress,
  Stack,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AssessmentIcon from '@mui/icons-material/Assessment'

interface SubscriptionTabProps {
  usermeta: any
}

const SubscriptionTab: React.FC<SubscriptionTabProps> = ({ usermeta }) => {
  const theme = useTheme()

  // Product access calculations
  const hasAlertAccess = usermeta?.products?.alerts
  const hasBacktestAccess = usermeta?.products?.backtest
  const alertProduct = usermeta?.products?.alerts
  const backtestProduct = usermeta?.products?.backtest

  // Get subscription name (prefer alert product, fallback to backtest)
  const subscriptionName = alertProduct?.name || backtestProduct?.name

  return (
    <Grid container spacing={3}>
      {/* Strategy Suite Subscription */}
      <Grid item xs={12}>
        <Card sx={{ height: 'fit-content', backgroundColor: theme.palette.background.custom2 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <MonetizationOnIcon /> Strategy Suite Subscription
              {subscriptionName && <Chip label={subscriptionName} color="primary" size="small" />}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {subscriptionName ? (
              <Typography variant="body2" color="text.secondary">
                Your current subscription plan provides access to the services below.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No active Strategy Suite subscription
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Alert Service */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 'fit-content', backgroundColor: theme.palette.background.custom2 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <NotificationsActiveIcon /> Alert Service
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {hasAlertAccess ? (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Active Alerts Allowed
                  </Typography>
                  <Typography variant="body1">{alertProduct.alertCount} alerts</Typography>
                </Box>

                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Notifications
                  </Typography>
                  <Typography variant="body1">
                    {alertProduct.notificationsRemaining || 0} of {alertProduct.notifications || 0}{' '}
                    remaining
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={
                      alertProduct.notifications > 0
                        ? ((alertProduct.notificationsRemaining || 0) /
                            alertProduct.notifications) *
                          100
                        : 0
                    }
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Renews each subscription period
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No active alert subscription
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Backtest Service */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: 'fit-content', backgroundColor: theme.palette.background.custom2 }}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <AssessmentIcon /> Backtest Service
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {hasBacktestAccess ? (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Credits This Month
                  </Typography>
                  <Typography variant="body1">
                    {(backtestProduct.accruedCost || 0).toFixed(2)} of{' '}
                    {backtestProduct.maxCost || 0} used
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={
                      backtestProduct.maxCost > 0
                        ? ((backtestProduct.accruedCost || 0) / backtestProduct.maxCost) * 100
                        : 0
                    }
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Renews each subscription period
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No active backtest subscription
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  )
}

export default SubscriptionTab
